import type { GridColDef, GridRowModel } from '@mui/x-data-grid-premium';
import type { Apollo } from 'src/gql/generated';
import type { LabelSubset, UserSubset, UserWithOrg } from 'src/types';

/** Display mode for document grid - flat list or relationship tree */
export type DisplayMode = 'flat' | 'relationship';

type DocumentVersionSubset = Pick<
  Apollo.DocumentVersion,
  | 'createdAt'
  | 'fileContentChangedAt'
  | 'id'
  | 'vectorsFailedAt'
  | 'vectorsSavedAt'
  | 'vectorsStartedAt'
  | '__typename'
>;
interface StaticFileVersionSubset extends Pick<
  Apollo.StaticFileVersion,
  | 'createdAt'
  | 'fileContentChangedAt'
  | 'fileExtractionFailedAt'
  | 'fileExtractionFinishedAt'
  | 'fileExtractionStartedAt'
  | 'vectorsFailedAt'
  | 'vectorsSavedAt'
  | 'vectorsStartedAt'
  | 'id'
  | '__typename'
> {
  fileLocation: Pick<Apollo.DocFile, 'id' | 'mimetype' | '__typename'>;
  /** Selected by the report rows, so the Status cell can show detection running. */
  signatureDetection?: Pick<
    Apollo.SignatureDetection,
    'failedAt' | 'finishedAt' | 'startedAt'
  > | null;
}
type ReportFileVersion = DocumentVersionSubset | StaticFileVersionSubset;

/** Review subset — includes outcome and the user who reviewed */
type RelationshipReviewSubset = Pick<
  Apollo.DocumentRelationshipReview,
  '__typename' | 'outcome'
> & {
  reviewedBy: Pick<Apollo.User, '__typename' | 'id' | 'displayName'> | null;
};

/** Subset of DocumentRelationship for parent relationship display */
export type ParentRelationshipSubset = Pick<
  Apollo.DocumentRelationship,
  | 'id'
  | 'parentDocumentId'
  | 'childDocumentId'
  | 'childRelationshipType'
  | 'llmReasonsMarkdown'
  | 'source'
  | 'sourceAgreementName'
> & {
  review: RelationshipReviewSubset | null;
};

/** Subset of DocumentRelationship for orphan relationship display (adds expectedDocumentName). */
export type OrphanRelationshipSubset = ParentRelationshipSubset &
  Pick<Apollo.DocumentRelationship, 'expectedDocumentName' | 'expectedDocumentAgreementDate'>;

/** Subset of DocumentRelationship for URL relationship display */
export type UrlRelationshipSubset = Pick<
  Apollo.DocumentRelationship,
  | 'id'
  | 'parentDocumentId'
  | 'childDocumentId'
  | 'childRelationshipType'
  | 'childUrl'
  | 'llmReasonsMarkdown'
  | 'source'
> & {
  review: RelationshipReviewSubset | null;
};

export interface ReportGridRow extends Pick<
  Apollo.Document,
  | 'id'
  | 'createdAt'
  | 'deletedAt'
  | 'lastAccessedByRequesterAt'
  | 'name'
  | 'requesterFavorited'
  | 'requesterRole'
  | 'status'
  | 'variant'
  | 'workflowFileRole'
  | '__typename'
  | 'coreAiPropertiesExtractionFinishedAt'
  | 'coreAiPropertiesExtractionFailedAt'
  | 'coreAiPropertiesExtractionStartedAt'
  | 'relationshipPropertiesExtractionHash'
  | 'relationshipMatchingStartedAt'
  | 'relationshipMatchingFinishedAt'
  | 'relationshipMatchingFailedReason'
> {
  aiQuestionIdsQueueing: UUID[];
  /** Server-computed count of expandable children (includes orphans and URL relationships) */
  childRowCount: number;
  /**
   * The actual document ID (UUID). In relationship tree mode, `id` is set to the tree row ID
   * for uniqueness, so use this field when you need the real document ID for API calls,
   * navigation, etc.
   */
  documentId: UUID;
  isCertificate: boolean;
  isSigned: boolean;
  labels: LabelSubset[];
  latestVersion: ReportFileVersion;
  owner: UserSubset & UserWithOrg;
  /** Parent tree row ID for deriving relationship context (null for flat mode and root rows) */
  parentTreeRowId: string | null;
  parties: Pick<Apollo.Party, 'id' | 'name' | '__typename'>[];
  questionJobs: ReportRowQuestionJob[];
  questionJobsByQuestionId: Record<string, ReportRowQuestionJob>;
  /** The relationship edge connecting this document to its expanded parent (null for root/flat rows) */
  relationship: ParentRelationshipSubset | null;
  /** Direction of this document relative to the expanded parent (null for root/flat rows) */
  relationshipDirection: Apollo.RelationshipDirection | null;
  type: Pick<Apollo.ContractType, 'id' | '__typename' | 'value'> | null;
  workflow: Pick<
    Apollo.Workflow,
    'id' | '__typename' | 'envelopeStatus' | 'isSignedOutsideAline' | 'wasSentForSig'
  > | null;
  /** Display label for MIME type (virtual column; from valueGetter / latestVersion). */
  fileType?: string;
  /** True while the Signed status is the one signature detection set. */
  isSignedAiGenerated?: boolean;
  /** Parent path for MUI DataGrid tree data - [parentId, selfId] or [selfId]. Optional until tree data is enabled. */
  parentPath?: string[];
}

interface ReviewSubset extends Pick<
  Apollo.ExtractionQuestionReview,
  '__typename' | 'reviewAction' | 'reviewedAt'
> {
  reviewedBy: UserSubset;
}
export type QuestionJobOption = Pick<
  Apollo.QuestionOption,
  'id' | '__typename' | 'color' | 'label' | 'order'
>;

export type ReportRowQuestion = Pick<
  Apollo.ExtractionQuestion,
  'id' | '__typename' | 'contentHash' | 'questionText' | 'variant' | 'type' | 'mode'
>;
export interface ReportRowQuestionJob extends Pick<
  Apollo.ExtractionJob,
  | 'id'
  | '__typename'
  | 'answer'
  | 'startedAt'
  | 'failedAt'
  | 'finishedAt'
  | 'lockedAt'
  | 'previousAnswer'
  | 'questionContentHashAtRun'
  | 'reasoning'
> {
  fileVersion: Pick<Apollo.FileVersion, 'id' | '__typename'> | null;
  locations: Array<
    | Pick<Apollo.AlineLocation, '__typename' | 'locationPath' | 'referenceText'>
    | Pick<Apollo.FileLocation, '__typename' | 'pageNum' | 'rect' | 'referenceText'>
  >;
  lockedBy: UserSubset | null;
  previousSelectedOptions: QuestionJobOption[];
  question: ReportRowQuestion;
  review: ReviewSubset | null;
  selectedOptions: QuestionJobOption[];
}

type ReportColTypename = Apollo.ReportColumnOption['__typename'];

export type ReportGridColDef = GridColDef & {
  __typename: ReportColTypename;
};

export type ReportGridStateColDef = GridColDef & {
  __typename: ReportColTypename;
};

export function getIsReportGridColDef(colDef: GridColDef): colDef is ReportGridColDef {
  return '__typename' in colDef && colDef.__typename !== undefined;
}

export function getIsReportGridRow(row: GridRowModel | null | undefined): row is ReportGridRow {
  /**
   * `row` can be nullish at runtime even though the grid types it as a valid
   * row model: `getSelectedRows()` yields `undefined` for selected IDs with no
   * underlying data (group-header / aggregation rows), and the `in` operator
   * throws on a nullish operand. Guard before narrowing.
   */
  if (!row) return false;
  return '__typename' in row && row.__typename === 'Document';
}

/** A group header row in grouped display mode (no document data). */
export interface GroupHeaderRow {
  __typename: 'GroupHeaderRow';
  /** Number of documents in this group (shown as the "(N)" count). */
  documentCount: number;
  /**
   * Identifier of the grouped column: a `ReportColumnFileField` name or an
   * `ExtractionQuestion` UUID — the same identifier space as grid columns,
   * `filterModel.field`, and `sortModel.field`.
   */
  groupFieldId: string;
  /** The group key value (e.g., 'Signed') */
  groupKey: string;
  /** Unique row ID */
  id: string;
  /** Parent path for MUI DataGrid tree data */
  parentPath: string[];
  /** Parent group ID if nested, null for top-level groups */
  parentTreeRowId: string | null;
  /**
   * Number of immediate child rows this group expands into (subgroups at an
   * intermediate level). Used as `getChildrenCount` for native nested lazy
   * loading. `null`/absent at the deepest level, where the immediate child rows
   * ARE the documents — readers fall back to `documentCount`.
   */
  childRowCount?: number | null;
}

export function getIsGroupHeaderRow(row: GridRowModel): row is GroupHeaderRow {
  return row.__typename === 'GroupHeaderRow';
}

/** An orphan relationship row where the linked document doesn't exist yet. */
export interface OrphanRelationshipRow {
  __typename: 'OrphanRelationshipRow';
  /** Unique row ID (includes orphan: prefix) */
  id: string;
  /** The orphaned relationship data */
  orphanedRelationship: OrphanRelationshipSubset;
  /** Direction of the MISSING document relative to the existing one */
  orphanedRelationshipDirection: Apollo.RelationshipDirection;
  /** Parent path for MUI DataGrid tree data */
  parentPath: string[];
  /** Parent tree row ID (the document this orphan relationship belongs to) */
  parentTreeRowId: string | null;
}

export function getIsOrphanRelationshipRow(row: GridRowModel): row is OrphanRelationshipRow {
  return row.__typename === 'OrphanRelationshipRow';
}

/** A URL relationship row where the child is a URL (not a document). */
export interface UrlRelationshipRow {
  __typename: 'UrlRelationshipRow';
  /** The URL of the referenced resource */
  childUrl: string;
  /** Unique row ID (includes url: prefix) */
  id: string;
  /** Parent path for MUI DataGrid tree data */
  parentPath: string[];
  /** Parent tree row ID (the document this URL relationship belongs to) */
  parentTreeRowId: string | null;
  /** The URL relationship data */
  urlRelationship: UrlRelationshipSubset;
}

export function getIsUrlRelationshipRow(row: ReportRow): row is UrlRelationshipRow {
  return row.__typename === 'UrlRelationshipRow';
}

/** Custom DataGrid row class for rows that should not be clickable (e.g. orphan/URL relationship rows). */
export const ROW_CLASS_NOT_CLICKABLE = 'row--not-clickable';

/** Named union of all row types that can appear in a report DataGrid. */
export type ReportGridGroupRow = GroupHeaderRow | OrphanRelationshipRow | UrlRelationshipRow;
export type ReportRow = ReportGridRow | ReportGridGroupRow;
